import { supabase } from "~/integrations/supabase/client";
import type {
  LotTraceabilityRow,
  PalletMovementHistoryRow,
  PalletSearchResult,
  PositionStatusResult,
  ProductInventoryDetail,
  ProductSearchResult,
  StockSummary,
} from "~/types/wms";

// Type for unified search results from database function
export interface UnifiedSearchRow {
  id: string;
  result_type: string;
  name: string;
  code: string | null;
  subtitle: string;
  extra_data: Record<string, unknown>;
}

export const stockConsultationService = {
  /**
   * Search products by SKU, name, barcode, or internal_id
   */
  async searchProducts(search: string): Promise<ProductSearchResult[]> {
    const { data, error } = await supabase.rpc("search_products_for_stock", {
      p_search: search,
      p_limit: 20,
    });

    if (error) throw error;
    return (data || []) as ProductSearchResult[];
  },

  /**
   * Get stock summary for a product
   * Returns available, reserved, and blocked quantities
   */
  async getStockSummary(productId: string): Promise<StockSummary | null> {
    const { data, error } = await supabase.rpc("get_stock_summary_by_product", {
      p_product_id: productId,
    });

    if (error) throw error;
    return data && data.length > 0 ? (data[0] as StockSummary) : null;
  },

  /**
   * Get inventory details by product (list of positions with inventory)
   */
  async getInventoryByProduct(
    productId: string
  ): Promise<ProductInventoryDetail[]> {
    const { data, error } = await supabase.rpc("get_inventory_by_product", {
      p_product_id: productId,
    });

    if (error) throw error;
    return (data || []) as ProductInventoryDetail[];
  },

  /**
   * Search pallets by barcode or code
   */
  async searchPalletByBarcode(barcode: string): Promise<PalletSearchResult[]> {
    const { data, error } = await supabase.rpc("search_pallet_by_barcode", {
      p_barcode: barcode,
    });

    if (error) throw error;
    return (data || []) as PalletSearchResult[];
  },

  /**
   * Get movement history for a pallet
   */
  async getPalletMovementHistory(
    palletId: string
  ): Promise<PalletMovementHistoryRow[]> {
    const { data, error } = await supabase.rpc("get_pallet_movement_history", {
      p_pallet_id: palletId,
    });

    if (error) throw error;
    return (data || []) as PalletMovementHistoryRow[];
  },

  /**
   * Get lot traceability - all locations where a lot exists
   */
  async getLotTraceability(lotNumber: string): Promise<LotTraceabilityRow[]> {
    const { data, error } = await supabase.rpc("get_lot_traceability", {
      p_lot_number: lotNumber,
    });

    if (error) throw error;
    return (data || []) as LotTraceabilityRow[];
  },

  /**
   * Get position status with content
   * Returns array of pallets at this position (one row per pallet with inventory)
   */
  async getPositionStatus(positionId: string): Promise<PositionStatusResult[]> {
    const { data, error } = await supabase.rpc("get_position_status", {
      p_position_id: positionId,
    });

    if (error) throw error;
    // Filter out rows with non-positive box_count (data integrity issues)
    return ((data || []) as PositionStatusResult[]).filter(
      (row) => row.box_count > 0
    );
  },

  /**
   * Search positions by code or barcode
   */
  async searchPositions(
    search: string
  ): Promise<{ id: string; full_code: string; barcode: string | null }[]> {
    const { data, error } = await supabase
      .from("wms_positions")
      .select("id, full_code, barcode")
      .or(`full_code.ilike.%${search}%,barcode.ilike.%${search}%`)
      .eq("is_active", true)
      .limit(20)
      .order("full_code");

    if (error) throw error;
    return data || [];
  },

  /**
   * Unified search across all stock data types (single RPC call)
   * Searches products, pallets, lots, positions, oils, and raw materials
   */
  async unifiedSearch(
    search: string,
    limitPerType: number = 10
  ): Promise<UnifiedSearchRow[]> {
    const { data, error } = await supabase.rpc("unified_stock_search", {
      p_search: search,
      p_limit: limitPerType,
    });

    if (error) throw error;
    return (data || []) as UnifiedSearchRow[];
  },

  /**
   * Get positions for hierarchical selection
   */
  async getPositionsForSelection(filters: {
    warehouseId?: string;
    hallwayId?: string;
    rackId?: string;
  }): Promise<
    {
      id: string;
      code: string;
      full_code: string;
      level: number;
      rack_id: string;
    }[]
  > {
    let query = supabase
      .from("wms_positions")
      .select(
        `
        id,
        code,
        full_code,
        level,
        rack_id,
        rack:wms_racks!inner(
          id,
          hallway:wms_hallways!inner(
            id,
            warehouse_id
          )
        )
      `
      )
      .eq("is_active", true);

    if (filters.rackId) {
      query = query.eq("rack_id", filters.rackId);
    } else if (filters.hallwayId) {
      query = query.eq("rack.hallway_id", filters.hallwayId);
    } else if (filters.warehouseId) {
      query = query.eq("rack.hallway.warehouse_id", filters.warehouseId);
    }

    const { data, error } = await query.order("full_code").limit(100);

    if (error) throw error;
    return (data || []).map((p) => ({
      id: p.id,
      code: p.code,
      full_code: p.full_code,
      level: p.level,
      rack_id: p.rack_id,
    }));
  },
};
